// client/src/config/pagePermissions.js
// PAGE-BASED ROLE PERMISSIONS FOR SCHOOL MANAGEMENT SYSTEM

// Define all available roles in the system
export const ROLES = {
  // System Level Roles
  SYSTEM_ADMIN: 'SystemAdmin',
  IT_MANAGER: 'ITManager',
  SALES_REP: 'SalesRep',
  SUPPORT_AGENT: 'SupportAgent',
  
  // School Level Roles
  SCHOOL_ADMIN: 'SchoolAdmin',
  PRINCIPAL: 'Principal',
  HEAD_MASTER: 'HeadMaster',
  
  // Academic Roles
  TEACHER: 'Teacher',
  CLASS_MASTER: 'ClassMaster',
  SUBJECT_HEAD: 'SubjectHead',
  SCHOOL_IT_COORDINATOR: 'SchoolITCoordinator',
  // Note: ClassMaster and SubjectHead are privileges that Teacher users can have
  
  // Support Staff Roles
  LIBRARIAN: 'Librarian',
  TRANSPORT_OFFICER: 'TransportOfficer',
  INVENTORY_MANAGER: 'InventoryManager',
  FINANCE_OFFICER: 'FinanceOfficer',
  ADMISSIONS_OFFICER: 'AdmissionsOfficer',
  
  // Student/Parent Roles
  STUDENT: 'Student',
  PARENT: 'Parent',
};

// Teacher privileges - additional privileges a Teacher user can have
export const TEACHER_PRIVILEGES = {
  CLASS_MASTER: 'ClassMaster', // Teacher with class management privileges
  SUBJECT_HEAD: 'SubjectHead'  // Teacher with subject management privileges (SEC phase only)
};

// Define page permissions - each page has specific role requirements (RAW mapping only)
// Define the raw page permissions mapping.
// NOTE: Do not enforce SchoolAdmin here; we will derive the final PAGE_PERMISSIONS below
// so that SchoolAdmin automatically has access to all non-super-admin routes.
//
// ONE ENTRY PER PATH. This is an object literal, so a repeated key does not
// merge with the first — it REPLACES it, silently, and the last one in the file
// wins. Sixty duplicate keys had accumulated here, and thirteen of them listed
// DIFFERENT roles from the entry they were shadowing, which meant the file said
// one thing in its documented section and did another. Teachers had lost
// /health/records and /reports/academic that way; a FinanceOfficer had lost
// /communication/announcements; and /id-card-workshop granted HeadMaster
// directly beneath a comment stating HeadMaster was excluded.
//
// They are merged now, each at the value that was actually in force, so this
// clean-up changed no one's access. Add a role to the existing entry rather
// than writing a second one; `no-dupe-keys` will catch a slip.
const RAW_PAGE_PERMISSIONS = {
  // ===== SUPER ADMIN PAGES =====
  '/super-admin/system-overview': [ROLES.SYSTEM_ADMIN],
  '/super-admin/schools': [ROLES.SYSTEM_ADMIN, ROLES.SALES_REP],
  '/super-admin/billing': [ROLES.SYSTEM_ADMIN],
  '/super-admin/system-settings': [ROLES.SYSTEM_ADMIN],
  '/super-admin/communications': [ROLES.SYSTEM_ADMIN],
  '/super-admin/system-users': [ROLES.SYSTEM_ADMIN],
  '/super-admin/users-access': [ROLES.SYSTEM_ADMIN],
  '/super-admin/security': [ROLES.SYSTEM_ADMIN],
  '/super-admin/support-tickets': [ROLES.SYSTEM_ADMIN, ROLES.SUPPORT_AGENT],
  '/super-admin/registration-queue': [ROLES.SYSTEM_ADMIN, ROLES.SUPPORT_AGENT],
  '/super-admin/onboarding-status': [ROLES.SYSTEM_ADMIN, ROLES.SALES_REP, ROLES.SUPPORT_AGENT],
  '/super-admin/super-admins': [ROLES.SYSTEM_ADMIN],
  '/super-admin/school-admins': [ROLES.SYSTEM_ADMIN],
  '/super-admin/global-settings': [ROLES.SYSTEM_ADMIN],
  '/super-admin/license-management': [ROLES.SYSTEM_ADMIN],
  '/super-admin/data-policies': [ROLES.SYSTEM_ADMIN],
  '/super-admin/subscription-plans': [ROLES.SYSTEM_ADMIN],
  '/super-admin/invoices': [ROLES.SYSTEM_ADMIN],
  '/super-admin/api-management': [ROLES.SYSTEM_ADMIN],
  '/super-admin/integrations': [ROLES.SYSTEM_ADMIN],
  '/super-admin/activity-logs': [ROLES.SYSTEM_ADMIN],
  '/super-admin/audit-trails': [ROLES.SYSTEM_ADMIN],
  '/super-admin/audit-log-export': [ROLES.SYSTEM_ADMIN],
  '/super-admin/it/dashboard': [ROLES.SYSTEM_ADMIN, ROLES.IT_MANAGER],
  '/super-admin/it/support-tickets': [ROLES.SYSTEM_ADMIN, ROLES.IT_MANAGER],
  '/super-admin/it/api-management': [ROLES.SYSTEM_ADMIN, ROLES.IT_MANAGER],
  '/super-admin/it/system-monitoring': [ROLES.SYSTEM_ADMIN, ROLES.IT_MANAGER],
  '/super-admin/it/backup-management': [ROLES.SYSTEM_ADMIN, ROLES.IT_MANAGER],
  // App route aliases for IT pages
  '/super-admin/it/maintenance': [ROLES.SYSTEM_ADMIN, ROLES.IT_MANAGER],
  '/super-admin/it/monitoring': [ROLES.SYSTEM_ADMIN, ROLES.IT_MANAGER],
  '/super-admin/it/backup': [ROLES.SYSTEM_ADMIN, ROLES.IT_MANAGER],
  '/super-admin/it/security': [ROLES.SYSTEM_ADMIN, ROLES.IT_MANAGER],
  '/super-admin/it/api': [ROLES.SYSTEM_ADMIN, ROLES.IT_MANAGER],

  // ===== SCHOOL ADMIN, PRINCIPAL, HEADMASTER PAGES =====
  '/dashboard': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/school-overview': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/quick-actions': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],

  // ===== CLASS MANAGEMENT =====
  '/class-management': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/class-management/add-class': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/class-management/manage-classes': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/class-management/class-assignments': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/class-management/class-reports': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],

  // ===== SUBJECT MANAGEMENT (SEC PHASE ONLY) =====
  '/subject-management': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/subject-management/add-subject': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/subject-management/manage-subjects': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/subject-management/subject-assignments': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/subject-management/subject-reports': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],

  // ===== STUDENT MANAGEMENT =====
  '/student-admissions': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.ADMISSIONS_OFFICER],
  '/student-directory': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.ADMISSIONS_OFFICER],
  '/attendance-summary': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/students-discipline': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/class-promotion-summary': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  // CONFLICT TO SETTLE: this entry said "HeadMaster excluded per requirements"
  // and listed only SchoolAdmin and Principal — but a second, later entry for
  // the same path added HeadMaster, and being later it was the one JavaScript
  // kept. HeadMaster has therefore had access all along, whatever the comment
  // said. The duplicate is now merged into this single entry AT THE VALUE THAT
  // WAS ALREADY IN FORCE, so nothing changed today; if the original requirement
  // still stands, drop ROLES.HEAD_MASTER here.
  '/id-card-workshop': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/student-transcripts': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],

  // ===== ACADEMICS =====
  '/evaluation-control-center': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/calendar': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/timetable': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  // Secondary only, so no HeadMaster: they run nursery/primary and the page
  // would refuse every class they can see.
  '/timetable-generator': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL],
  '/grading-scales': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/report-card-studio': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],

  // ===== TEACHING & LEARNING =====
  '/teacher/marks': [ ROLES.TEACHER],
  '/teacher/attendance-recording': [ ROLES.TEACHER],
  // Backward-compatibility alias kept but restricted to ClassMaster privilege
  '/teacher/class-discipline': [ TEACHER_PRIVILEGES.CLASS_MASTER], // Alias; use '/classmaster/class-discipline'
  '/classmaster/class-discipline': [ROLES.CLASS_MASTER],
  '/classmaster/class-academic-performance': [ROLES.CLASS_MASTER], // Teachers with ClassMaster privilege
  '/subjecthead/subject-report': [ ROLES.SUBJECT_HEAD], // Teachers with SubjectHead privilege, SEC PHASE ONLY
  '/subjecthead/subject-performance-matrix': [ROLES.SUBJECT_HEAD], // Teachers with SubjectHead privilege, SEC PHASE ONLY

  // ===== FINANCE =====
  '/fee-types': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.FINANCE_OFFICER],
  '/fee-structure-manager': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.FINANCE_OFFICER],
  '/payment-processing': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.FINANCE_OFFICER],
  '/manage-fee-payments': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.FINANCE_OFFICER],
  '/finance/subscription-renewal': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.FINANCE_OFFICER],
  '/expenses': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.FINANCE_OFFICER],
  '/budgets': [ROLES.SCHOOL_ADMIN, ROLES.FINANCE_OFFICER],
  '/financial-reports': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.FINANCE_OFFICER],
  '/salary-management': [ROLES.SCHOOL_ADMIN],
  '/payroll': [ROLES.SCHOOL_ADMIN, ROLES.FINANCE_OFFICER],
  '/tuition': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.FINANCE_OFFICER],
  '/facilities/transport-routes': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.TRANSPORT_OFFICER],
  '/facilities/transport-dashboard': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.TRANSPORT_OFFICER],
  '/facilities/inventory-control': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.INVENTORY_MANAGER],

  // ===== HEALTH MANAGEMENT =====
  '/health/records': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/health/incidents': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],

  // ===== REPORTS =====
  '/reports/academic': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/reports/transcripts': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],

  // ===== COMMUNICATION =====
  '/communication/announcements': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/communication/emergency-alerts': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.TEACHER],
  '/communication/notification-center': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.TEACHER, ROLES.STUDENT, ROLES.PARENT],
  '/communication/messages': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.TEACHER, ROLES.STUDENT, ROLES.PARENT],

  // ===== SUPPORT =====
  '/support/submit-ticket': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/support/my-tickets': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/support/help-center': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],

  // ===== SCHOOL SETUP & CONFIGURATION =====
  '/school-setup-and-configuration/school-settings': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/school-setup-and-configuration/communication-settings': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/school-setup-and-configuration/backup-security': [ROLES.SYSTEM_ADMIN, ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/school-setup-and-configuration/academic-sessions': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],

  // ===== ROLE PERMISSIONS (SchoolAdmin only) =====
  'staff/role-permissions': [ROLES.SCHOOL_ADMIN],
  '/role-permissions': [ROLES.SCHOOL_ADMIN],
  

  // ===== STUDENT PAGES =====
  '/student/messages': [ROLES.STUDENT],
  '/student/profile': [ROLES.STUDENT],

  // ===== PARENT PAGES =====
  '/parent/dashboard': [ROLES.PARENT],
  '/parent/children-academics': [ROLES.PARENT],
  '/parent/attendance': [ROLES.PARENT],
  '/parent/fees-payments': [ROLES.PARENT],
  '/parent/messages': [ROLES.PARENT],
  '/parent/guardian-dashboard': [ROLES.PARENT],

  // ===== COMMON PAGES =====
  '/my-profile': [ROLES.SYSTEM_ADMIN, ROLES.IT_MANAGER, ROLES.SALES_REP, ROLES.SUPPORT_AGENT, ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.TEACHER, ROLES.FINANCE_OFFICER, ROLES.LIBRARIAN, ROLES.TRANSPORT_OFFICER, ROLES.STUDENT, ROLES.PARENT],

  // ===== STUDENT MANAGEMENT PAGES =====
  '/discipline': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/parent-access': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],

  // ===== STAFF MANAGEMENT =====
  '/user-list': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/user-form': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/user-details': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/staff-evaluation': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/performance-reviews': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/training-development': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/leave-management': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  
  // Alternative Staff Management Routes for compatibility
  '/staff/user-list': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/staff/users/new': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/staff/role-management': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/staff/evaluations': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/staff/performance-reviews': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/staff/training': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/staff/leave': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  
  '/evaluations': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  
  // Additional Staff Management Routes for role menu compatibility
  

  // ===== ACADEMICS PAGES =====
  '/subjects': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/classes': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/exams': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/grading': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/promotion': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/academics/timetable-designer': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/academics/evaluation-control-center': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/academics/subject-hub': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/academics/report-card-studio': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],

  // ===== ACADEMIC MANAGEMENT PAGES =====
  '/academic-years': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/exam-control-center': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],

  // ===== FINANCE PAGES =====
  '/finance/fee-structures': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.FINANCE_OFFICER],
  '/finance/payments': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.FINANCE_OFFICER],
  '/finance/manage-fee-payments': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.FINANCE_OFFICER],
  '/finance/reports': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.FINANCE_OFFICER],

  // ===== LIBRARY PAGES =====
  '/facilities/library-dashboard': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.LIBRARIAN],
  '/facilities/library/catalog': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.LIBRARIAN],
  '/facilities/library/circulation': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.LIBRARIAN],
  '/facilities/library/issues': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.LIBRARIAN],

  // ===== COMMUNICATION PAGES =====
  '/communication/notifications': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.TEACHER, ROLES.STUDENT, ROLES.PARENT],
  '/announcements': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/messaging': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.TEACHER, ROLES.STUDENT, ROLES.PARENT],
  '/notifications': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.TEACHER, ROLES.STUDENT, ROLES.PARENT],

  // ===== REPORTS PAGES =====
  '/reports/financial': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/report-generation': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],

  // ===== ADMIN MANAGEMENT PAGES =====
  '/school-management': [ROLES.SCHOOL_ADMIN],
  '/role-management': [ROLES.SCHOOL_ADMIN],

  // ===== LEGACY SCHOOL SETTINGS PAGES (for backward compatibility) =====
  '/school-settings/general': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/school-settings/academic': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/school-settings/communication': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/school-settings/portal': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/school-settings/backup': [ ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],

  // ===== TEACHER SPECIFIC PAGES =====
  '/teacher/dashboard': [ ROLES.TEACHER],
  '/teacher/attendance': [ROLES.TEACHER],
  '/teacher/timetable': [ROLES.TEACHER],
  '/teacher/messages': [ROLES.TEACHER],
  '/teacher/student-grading': [ ROLES.TEACHER],

  // ===== DEBUG (Admin only) =====
  '/debug/effective-access': [ROLES.SCHOOL_ADMIN],

  // ===== PHASE MANAGEMENT PAGES =====
  '/phase-dashboard': [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
  '/phase-switcher': [ROLES.SCHOOL_ADMIN],
};

// Derive final PAGE_PERMISSIONS so SchoolAdmin has access to ALL non-super-admin routes
// without having to repeat it in every entry above. This keeps the config DRY and consistent.
export const PAGE_PERMISSIONS = Object.fromEntries(
  Object.entries(RAW_PAGE_PERMISSIONS).map(([route, roles]) => {
    const base = Array.isArray(roles) ? roles : [];
    const shouldAddSchoolAdmin = !(
      route.startsWith('/super-admin/')
      || route.startsWith('/teacher/')
      || route.startsWith('/classmaster/')
      || route.startsWith('/subjecthead/')
      || route.startsWith('/parent/')
    );
    const addSchoolAdmin = shouldAddSchoolAdmin ? [ROLES.SCHOOL_ADMIN] : [];
    const normalized = Array.from(new Set([...base, ...addSchoolAdmin]));
    return [route, normalized];
  })
);

// Helper function to check if a user has access to a specific page
export const hasPageAccess = (userRoles, pagePath, userPrivileges = []) => {
  if (!userRoles) return false;
  const rolesArray = Array.isArray(userRoles) ? userRoles : [userRoles];
  // Include teacher privileges in the role check
  const allRoles = [...rolesArray, ...userPrivileges];
  const expanded = getInheritedRoles(allRoles);

  if (!pagePath) return false;
  const cleanPath = String(pagePath)
    .split('#')[0]
    .split('?')[0]
    .trim();
  const normalizedPath = cleanPath === '/' ? '/' : cleanPath.replace(/\/+$/, '');

  // 1) Exact match
  const exactRequiredRoles = PAGE_PERMISSIONS[normalizedPath];
  if (Array.isArray(exactRequiredRoles)) {
    return expanded.some((role) => exactRequiredRoles.includes(role));
  }

  // 2) Longest prefix match (helps for nested routes)
  let bestMatchRoles = null;
  let bestMatchLen = -1;
  for (const [key, roles] of Object.entries(PAGE_PERMISSIONS)) {
    if (!key) continue;
    const k = key === '/' ? '/' : String(key).replace(/\/+$/, '');
    if (normalizedPath === k || normalizedPath.startsWith(k + '/')) {
      if (k.length > bestMatchLen) {
        bestMatchLen = k.length;
        bestMatchRoles = roles;
      }
    }
  }

  if (!Array.isArray(bestMatchRoles)) return false;
  return expanded.some((role) => bestMatchRoles.includes(role));
};

// Helper function to get all accessible pages for a user
export const getAccessiblePages = (userRoles) => {
  if (!userRoles || !Array.isArray(userRoles)) return [];
  
  return Object.keys(PAGE_PERMISSIONS).filter(pagePath => 
    hasPageAccess(userRoles, pagePath)
  );
};

// Helper function to check if user has any of the specified roles
export const hasAnyRole = (userRoles, requiredRoles) => {
  if (!userRoles) return false;
  const rolesArray = Array.isArray(userRoles) ? userRoles : [userRoles];
  const expanded = getInheritedRoles(rolesArray);
  if (!requiredRoles || !Array.isArray(requiredRoles)) return false;
  return expanded.some(role => requiredRoles.includes(role));
};

// Helper function to check if user has all of the specified roles
export const hasAllRoles = (userRoles, requiredRoles) => {
  if (!userRoles) return false;
  const rolesArray = Array.isArray(userRoles) ? userRoles : [userRoles];
  const expanded = getInheritedRoles(rolesArray);
  if (!requiredRoles || !Array.isArray(requiredRoles)) return false;
  return requiredRoles.every(role => expanded.includes(role));
};

// Role hierarchy for inheritance
export const ROLE_HIERARCHY = {
  [ROLES.SYSTEM_ADMIN]: [],
  [ROLES.IT_MANAGER]: [],
  [ROLES.SALES_REP]: [],
  [ROLES.SUPPORT_AGENT]: [],
  [ROLES.SCHOOL_ADMIN]: [],
  [ROLES.PRINCIPAL]: [],
  [ROLES.HEAD_MASTER]: [],
  [ROLES.TEACHER]: [],
  // Teacher privilege roles should inherit Teacher access
  [TEACHER_PRIVILEGES.CLASS_MASTER]: [ROLES.TEACHER],
  [TEACHER_PRIVILEGES.SUBJECT_HEAD]: [ROLES.TEACHER],
  [ROLES.FINANCE_OFFICER]: [],
  [ROLES.LIBRARIAN]: [],
  [ROLES.TRANSPORT_OFFICER]: [],
  [ROLES.STUDENT]: [],
  [ROLES.PARENT]: []
};

// Get all inherited roles for a user
export const getInheritedRoles = (userRoles) => {
  if (!userRoles) return [];
  const base = Array.isArray(userRoles) ? userRoles : [userRoles];
  const inheritedRoles = new Set(base);
  base.forEach(role => {
    if (ROLE_HIERARCHY[role]) {
      ROLE_HIERARCHY[role].forEach(inheritedRole => {
        inheritedRoles.add(inheritedRole);
      });
    }
  });
  return Array.from(inheritedRoles);
};